import React, { useCallback, useEffect, useMemo, useState } from "react";
import { v4 as uuidv4 } from "uuid";
import dayjs from "dayjs";
import {
  App,
  Form,
  Row,
  Col,
  InputNumber,
  Select,
  Switch,
  Checkbox,
  Button,
  Progress,
  DatePicker,
  Tabs,
  Table,
} from "antd";
import { StarOutlined, StarFilled } from "@ant-design/icons";

import DashedTooltip from "components/UI/DashedTooltip";
import useTimer from "hooks/useTimer";
import useTrackSignals from "hooks/useTrackSignals";
import socket from "utils/socket";
import GenericCard from "components/UI/GenericCard/GenericCard";
import AntTable from "components/UI/AntTable";
import getRangeFilterProps from "utils/tableRangeFilter";
import { applyBenjaminiHochberg } from "utils/benjaminiHochberg";
import { apiGetModelsList } from "api/apiAiModels";
import { apiGetAllSymbols } from "api/apiSymbols";
import { apiSearchOptionsLeaderboard } from "api/apiPredictions";
import { formatMomentDurationFull, returnUpDownClassname } from "utils/helpers";
import OptionsSignalSection from "components/AiOptionsAnalysis/OptionsSignalSection";

const { RangePicker } = DatePicker;

const STORAGE_KEY = "optionsLeaderboardParams";
const DEFAULT_START_DATE = "2018-01-01";
const DEFAULT_TARGET_GAIN_PERCENT = 3;
const DEFAULT_MIN_DAYS = 2;
const DEFAULT_MAX_DAYS = 5;

const loadSavedParams = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

const saveParams = (params) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(params));
  } catch {
    // localStorage unavailable/full -- persistence is a convenience, not required
  }
};

// A full 2018-today, all-symbols/all-models sweep has thousands of combos --
// some will look great purely by chance. These thresholds favor combos with
// enough sample size to trust, a real (not noise-level) edge over baseline,
// and positive, comfortably-profitable expectancy.
const RECOMMENDED_FILTERS = {
  completeSignals: [{ from: 30 }],
  pValue: [{ to: 0.05 }],
  edgeLift: [{ from: 1.3 }],
  expectancy: [{ from: 0 }],
  profitFactor: [{ from: 1.5 }],
  isCorrupted: [false],
};

const OptionsLeaderboard = () => {
  const { message } = App.useApp();
  const { isTracked, toggleTracking } = useTrackSignals();
  const [togglingTrackingKey, setTogglingTrackingKey] = useState(null);
  const [form] = Form.useForm();

  const [modelsList, setModelsList] = useState([]);
  const [symbolsList, setSymbolsList] = useState([]);
  const [allModels, setAllModels] = useState(
    () => loadSavedParams()?.allModels ?? true,
  );
  const [allSymbols, setAllSymbols] = useState(
    () => loadSavedParams()?.allSymbols ?? true,
  );
  const [initialFormValues] = useState(() => {
    const saved = loadSavedParams();
    return {
      targetGainPercent:
        saved?.targetGainPercent ?? DEFAULT_TARGET_GAIN_PERCENT,
      minDays: saved?.minDays ?? DEFAULT_MIN_DAYS,
      maxDays: saved?.maxDays ?? DEFAULT_MAX_DAYS,
      mwfExpiriesEnabled: saved?.mwfExpiriesEnabled ?? false,
      excludeCorrupted: saved?.excludeCorrupted ?? true,
      modelIds: saved?.modelIds,
      symbolIds: saved?.symbolIds,
      // End date is always "today" -- never read from storage, never editable.
      dates: [dayjs(saved?.startDate || DEFAULT_START_DATE), dayjs()],
    };
  });

  const [results, setResults] = useState([]);
  const [isPending, setIsPending] = useState(false);
  const [progress, setProgress] = useState(null);
  const [isError, setIsError] = useState(false);
  const { startTimer, stopTimer, time } = useTimer();
  const [estimatedTime, setEstimatedTime] = useState(null);
  const [filteredInfo, setFilteredInfo] = useState({});

  const [loadedAnalyses, setLoadedAnalyses] = useState([]);
  const [selectedAnalysisTab, setSelectedAnalysisTab] = useState("");

  useEffect(() => {
    document.title = "Options Leaderboard | Dindicator Dashboard";
  }, []);

  useEffect(() => {
    const getModelsList = async () => {
      const list = await apiGetModelsList("1d");
      setModelsList(list);
    };
    getModelsList();
  }, []);

  useEffect(() => {
    const getSymbolsList = async () => {
      const list = await apiGetAllSymbols();
      setSymbolsList(list.filter((symbol) => symbol.enabled));
    };
    getSymbolsList();
  }, []);

  useEffect(() => {
    if (time === null || !progress) return;
    setEstimatedTime((100 / progress) * time - time);
  }, [progress, time]);

  const onToggleAllModels = (e) => {
    setAllModels(e.target.checked);
    if (e.target.checked) {
      form.setFieldValue("modelIds", undefined);
      form.validateFields(["modelIds"]).catch(() => {});
    }
  };

  const onToggleAllSymbols = (e) => {
    setAllSymbols(e.target.checked);
    if (e.target.checked) {
      form.setFieldValue("symbolIds", undefined);
      form.validateFields(["symbolIds"]).catch(() => {});
    }
  };

  const handleRunLeaderboard = async (values) => {
    const startDate =
      values.dates?.[0]?.format("YYYY-MM-DD") || DEFAULT_START_DATE;
    const endDate =
      values.dates?.[1]?.format("YYYY-MM-DD") || dayjs().format("YYYY-MM-DD");

    // endDate is deliberately not saved -- restoring a search should always
    // land on today, not whatever date happened to be "today" back then.
    saveParams({
      targetGainPercent: values.targetGainPercent,
      minDays: values.minDays,
      maxDays: values.maxDays,
      mwfExpiriesEnabled: values.mwfExpiriesEnabled,
      excludeCorrupted: values.excludeCorrupted,
      modelIds: values.modelIds,
      symbolIds: values.symbolIds,
      allModels,
      allSymbols,
      startDate,
    });

    setResults([]);
    setIsError(false);
    setProgress(0);
    setIsPending(true);
    startTimer();

    const uuid = uuidv4();
    // The POST can resolve before the last socket batches land (separate
    // connections) -- unsubscribing then dropped every row of a short
    // picked-symbols run. Stop listening on the server's `done` emit instead,
    // with a timeout fallback for runs that never send one (errors, 0 combos).
    let fallbackTimeout = null;
    const unsubscribe = () => {
      clearTimeout(fallbackTimeout);
      socket.off(uuid, onSocketData);
    };
    const onSocketData = (socketData) => {
      setResults((prevState) => [...socketData.results, ...prevState]);
      setProgress(socketData.progress);
      if (socketData.done) unsubscribe();
    };
    socket.on(uuid, onSocketData);

    try {
      await apiSearchOptionsLeaderboard({
        uuid,
        modelIds: allModels ? undefined : values.modelIds,
        symbolIds: allSymbols ? undefined : values.symbolIds,
        startDate,
        endDate,
        targetGainPercent: values.targetGainPercent,
        minDays: values.minDays,
        maxDays: values.maxDays,
        mwfExpiriesEnabled: values.mwfExpiriesEnabled,
        excludeCorrupted: values.excludeCorrupted,
      });
      // Completion is the resolved POST, not any particular socket message --
      // don't depend on a terminal 100% emit actually arriving.
      setProgress(100);
    } catch (err) {
      setIsError(true);
      message.error(err?.response?.data?.message || "Leaderboard run failed");
    } finally {
      fallbackTimeout = setTimeout(unsubscribe, 10000);
      setIsPending(false);
      stopTimer();
    }
  };

  // Every result row is one model tested against one symbol -- run enough
  // models on the same symbol and some will clear p < 0.05 by chance alone.
  // Grouping by symbol (rather than the whole sweep) matches the actual
  // question being asked per row: "of the models I tried on THIS stock, which
  // ones are real." Kept alongside the raw p-value rather than replacing it,
  // since both are meaningful on their own.
  const resultsWithFdr = useMemo(
    () =>
      applyBenjaminiHochberg(results, {
        groupBy: (row) => row.symbol,
        getPValue: (row) => row.metrics?.edge?.pValue,
      }),
    [results],
  );

  const sortByNumber = useCallback((a, b) => {
    if (a === b) return 0;
    if (a === null || a === undefined) return -1;
    if (b === null || b === undefined) return 1;
    return a > b ? 1 : -1;
  }, []);

  const onAnalyze = (row) => {
    const _id = `${row.symbol}-${row.model._id}-${row.startDate}-${row.endDate}-leaderboard`;
    setLoadedAnalyses((prevState) => {
      if (prevState.find((item) => item._id === _id)) return prevState;
      return [
        {
          _id,
          symbol: row.symbol,
          model: { _id: row.model._id, name: row.model.name },
          startDate: row.startDate,
          endDate: row.endDate,
          initialParams: row.params,
        },
        ...prevState,
      ];
    });
    setSelectedAnalysisTab(_id);
  };

  const onToggleTracking = async (row) => {
    const rowKey = `${row.symbol}-${row.model?._id}`;
    setTogglingTrackingKey(rowKey);
    try {
      const added = await toggleTracking(
        row.symbol,
        row.model._id,
        row.startDate,
        row.params,
      );
      message.success(
        added
          ? `Tracking ${row.symbol} - ${row.model.name} signals`
          : `Stopped tracking ${row.symbol} - ${row.model.name}`,
      );
    } catch (err) {
      message.error(
        err?.response?.data?.message ||
          err.message ||
          "Failed to update tracking",
      );
    } finally {
      setTogglingTrackingKey(null);
    }
  };

  const onRemoveAnalysis = (resultId) => {
    setLoadedAnalyses((prevState) =>
      prevState.filter((item) => item._id !== resultId),
    );
    if (resultId === selectedAnalysisTab) {
      setSelectedAnalysisTab(loadedAnalyses[0]?._id);
    }
  };

  const clearLoadedAnalyses = () => setLoadedAnalyses([]);

  const onEditAnalysisTab = (targetKey, action) => {
    if (action === "remove") onRemoveAnalysis(targetKey);
  };

  const analysisTabItems = useMemo(
    () =>
      loadedAnalyses.map((item) => ({
        key: item._id,
        label: `${item.symbol} - ${item.model.name}`,
        children: <OptionsSignalSection results={item} />,
      })),
    [loadedAnalyses],
  );

  const columns = [
    {
      title: "",
      key: "analyze",
      dataIndex: "",
      render: (row) => (
        <Button size="small" onClick={() => onAnalyze(row)}>
          Analyze
        </Button>
      ),
    },
    {
      title: "",
      key: "track",
      dataIndex: "",
      render: (row) => {
        const rowKey = `${row.symbol}-${row.model?._id}`;
        const tracked = isTracked(row.symbol, row.model?._id);
        return (
          <Button
            size="small"
            type={tracked ? "primary" : "default"}
            icon={tracked ? <StarFilled /> : <StarOutlined />}
            loading={togglingTrackingKey === rowKey}
            onClick={() => onToggleTracking(row)}
          >
            {tracked ? "Tracking" : "Track signals"}
          </Button>
        );
      },
    },
    {
      title: "Symbol",
      dataIndex: "symbol",
      sorter: { compare: (a, b) => sortByNumber(a.symbol, b.symbol) },
    },
    {
      title: "Model",
      dataIndex: "model",
      render: (model) => model?.name,
      sorter: {
        compare: (a, b) => sortByNumber(a.model?.name, b.model?.name),
      },
    },
    {
      title: "Last signal",
      key: "lastSignalDate",
      dataIndex: "lastSignalDate",
      render: (text) => (text ? dayjs(text).format("DD/MM/YY") : "—"),
      sorter: {
        compare: (a, b) => sortByNumber(a.lastSignalDate, b.lastSignalDate),
      },
    },
    {
      title: "Success Rate",
      key: "successRate",
      dataIndex: ["metrics", "successRate"],
      render: (text) => <span>{text}%</span>,
      sorter: {
        compare: (a, b) =>
          sortByNumber(a.metrics.successRate, b.metrics.successRate),
      },
      ...getRangeFilterProps(["metrics", "successRate"]),
      filteredValue: filteredInfo.successRate,
    },
    {
      title: "Signals",
      key: "completeSignals",
      dataIndex: ["metrics", "completeSignals"],
      sorter: {
        compare: (a, b) =>
          sortByNumber(a.metrics.completeSignals, b.metrics.completeSignals),
      },
      ...getRangeFilterProps(["metrics", "completeSignals"]),
      filteredValue: filteredInfo.completeSignals,
    },
    {
      title: "Edge Lift",
      key: "edgeLift",
      dataIndex: ["metrics", "edge", "edgeLift"],
      render: (text) => (text === null ? "N/A" : `${text}x`),
      sorter: {
        compare: (a, b) =>
          sortByNumber(a.metrics.edge.edgeLift, b.metrics.edge.edgeLift),
      },
      ...getRangeFilterProps(["metrics", "edge", "edgeLift"]),
      filteredValue: filteredInfo.edgeLift,
    },
    {
      title: "Edge vs Baseline",
      key: "edgeAbsolute",
      dataIndex: ["metrics", "edge", "edgeAbsolute"],
      render: (text) => (
        <span className={returnUpDownClassname(text)}>{text}pp</span>
      ),
      sorter: {
        compare: (a, b) =>
          sortByNumber(
            a.metrics.edge.edgeAbsolute,
            b.metrics.edge.edgeAbsolute,
          ),
      },
      ...getRangeFilterProps(["metrics", "edge", "edgeAbsolute"]),
      filteredValue: filteredInfo.edgeAbsolute,
    },
    {
      title: "Significant",
      key: "isSignificant",
      dataIndex: ["metrics", "edge", "isSignificant"],
      render: (text) => (
        <span className={text ? "green" : "red"}>{text ? "Yes" : "No"}</span>
      ),
      filterMultiple: false,
      filters: [
        { text: "Yes", value: true },
        { text: "No", value: false },
      ],
      onFilter: (value, record) => record.metrics.edge.isSignificant === value,
      filteredValue: filteredInfo.isSignificant,
    },
    {
      title: "Significance",
      key: "pValue",
      dataIndex: ["metrics", "edge", "pValue"],
      render: (text, record) => (
        <span>
          z={record.metrics.edge.zScore} (p={text})
        </span>
      ),
      sorter: {
        compare: (a, b) =>
          sortByNumber(a.metrics.edge.pValue, b.metrics.edge.pValue),
      },
      ...getRangeFilterProps(["metrics", "edge", "pValue"]),
      filteredValue: filteredInfo.pValue,
    },
    {
      title: (
        <DashedTooltip text="Benjamini-Hochberg FDR-adjusted p-value, computed per symbol across every model tested on it -- accounts for the chance that some models clear p < 0.05 by luck alone when many are tried on the same stock.">
          Adjusted p (FDR)
        </DashedTooltip>
      ),
      key: "pValueAdjusted",
      dataIndex: "pValueAdjusted",
      render: (text) => (text === null ? "N/A" : text.toFixed(4)),
      sorter: {
        compare: (a, b) => sortByNumber(a.pValueAdjusted, b.pValueAdjusted),
      },
      ...getRangeFilterProps(["pValueAdjusted"]),
      filteredValue: filteredInfo.pValueAdjusted,
    },
    {
      title: (
        <DashedTooltip text="Whether the FDR-adjusted p-value still clears the 0.05 threshold. Can differ from 'Significant' (which uses the raw, unadjusted p-value).">
          Significant (FDR)
        </DashedTooltip>
      ),
      key: "isSignificantAdjusted",
      dataIndex: "isSignificantAdjusted",
      render: (text) => (
        <span className={text ? "green" : "red"}>{text ? "Yes" : "No"}</span>
      ),
      filterMultiple: false,
      filters: [
        { text: "Yes", value: true },
        { text: "No", value: false },
      ],
      onFilter: (value, record) => record.isSignificantAdjusted === value,
      filteredValue: filteredInfo.isSignificantAdjusted,
    },
    {
      title: "Corrupted",
      key: "isCorrupted",
      dataIndex: "isCorrupted",
      render: (text) => (
        <span className={text ? "red" : "green"}>{text ? "Yes" : "No"}</span>
      ),
      filterMultiple: false,
      filters: [
        { text: "Yes", value: true },
        { text: "No", value: false },
      ],
      onFilter: (value, record) => Boolean(record.isCorrupted) === value,
      filteredValue: filteredInfo.isCorrupted,
    },
    {
      title: "Avg bars to hit",
      key: "avgDaysToHit",
      dataIndex: ["metrics", "avgDaysToHit"],
      sorter: {
        compare: (a, b) =>
          sortByNumber(a.metrics.avgDaysToHit, b.metrics.avgDaysToHit),
      },
      ...getRangeFilterProps(["metrics", "avgDaysToHit"]),
      filteredValue: filteredInfo.avgDaysToHit,
    },
    {
      title: "Avg Win",
      key: "avgWinReturn",
      dataIndex: ["metrics", "expectancy", "avgWinReturn"],
      render: (text) => (
        <span className={returnUpDownClassname(text)}>{text}%</span>
      ),
      sorter: {
        compare: (a, b) =>
          sortByNumber(
            a.metrics.expectancy.avgWinReturn,
            b.metrics.expectancy.avgWinReturn,
          ),
      },
      ...getRangeFilterProps(["metrics", "expectancy", "avgWinReturn"]),
      filteredValue: filteredInfo.avgWinReturn,
    },
    {
      title: "Expectancy",
      key: "expectancy",
      dataIndex: ["metrics", "expectancy", "expectancy"],
      render: (text) => (
        <span className={returnUpDownClassname(text)}>{text}%</span>
      ),
      sorter: {
        compare: (a, b) =>
          sortByNumber(
            a.metrics.expectancy.expectancy,
            b.metrics.expectancy.expectancy,
          ),
      },
      ...getRangeFilterProps(["metrics", "expectancy", "expectancy"]),
      filteredValue: filteredInfo.expectancy,
    },
    {
      title: "Profit Factor",
      key: "profitFactor",
      dataIndex: ["metrics", "expectancy", "profitFactor"],
      render: (text) => (text === null ? "N/A" : text),
      sorter: {
        compare: (a, b) =>
          sortByNumber(
            a.metrics.expectancy.profitFactor,
            b.metrics.expectancy.profitFactor,
          ),
      },
      ...getRangeFilterProps(["metrics", "expectancy", "profitFactor"]),
      filteredValue: filteredInfo.profitFactor,
    },
  ];

  return (
    <div className="ai-magician-wrapper">
      <h2>Options Leaderboard</h2>
      <br />
      <div className="cards-wrapper">
        <GenericCard title="Run parameters">
          <Form
            form={form}
            layout="vertical"
            onFinish={handleRunLeaderboard}
            initialValues={initialFormValues}
          >
            <Row gutter={16}>
              <Col xs={24} sm={8}>
                <Form.Item
                  label="Models"
                  name="modelIds"
                  rules={[
                    {
                      required: !allModels,
                      message: "Pick models or check All models",
                    },
                  ]}
                >
                  <Select
                    mode="multiple"
                    allowClear
                    showSearch
                    disabled={allModels}
                    placeholder="Pick models"
                    optionFilterProp="label"
                    filterOption={(input, option) =>
                      (option?.label ?? "")
                        .toLowerCase()
                        .includes(input.toLowerCase())
                    }
                    options={modelsList.map((model) => ({
                      label: model.name,
                      value: model._id,
                    }))}
                  />
                </Form.Item>
                <Checkbox checked={allModels} onChange={onToggleAllModels}>
                  All models ({modelsList.length})
                </Checkbox>
              </Col>
              <Col xs={24} sm={8}>
                <Form.Item
                  label="Symbols"
                  name="symbolIds"
                  rules={[
                    {
                      required: !allSymbols,
                      message: "Pick symbols or check All symbols",
                    },
                  ]}
                >
                  <Select
                    mode="multiple"
                    allowClear
                    showSearch
                    disabled={allSymbols}
                    placeholder="Pick symbols"
                    optionFilterProp="label"
                    filterOption={(input, option) =>
                      (option?.label ?? "")
                        .toLowerCase()
                        .includes(input.toLowerCase())
                    }
                    options={symbolsList.map((symbol) => ({
                      label: symbol.ticker,
                      value: symbol._id,
                    }))}
                  />
                </Form.Item>
                <Checkbox checked={allSymbols} onChange={onToggleAllSymbols}>
                  All symbols ({symbolsList.length})
                </Checkbox>
              </Col>
              <Col xs={24} sm={8}>
                <Form.Item
                  label="Date range"
                  name="dates"
                  tooltip="Restoring a saved search always resets the end date to today."
                  rules={[{ required: true, message: "Pick a date range" }]}
                >
                  <RangePicker style={{ width: "100%" }} />
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={16}>
              <Col xs={12} sm={4}>
                <Form.Item
                  label="Target gain %"
                  name="targetGainPercent"
                  rules={[{ required: true, message: "Required" }]}
                >
                  <InputNumber min={0.1} step={0.5} style={{ width: "100%" }} />
                </Form.Item>
              </Col>
              <Col xs={12} sm={4}>
                <Form.Item
                  label="Min bars"
                  name="minDays"
                  rules={[{ required: true, message: "Required" }]}
                >
                  <InputNumber min={1} step={1} style={{ width: "100%" }} />
                </Form.Item>
              </Col>
              <Col xs={12} sm={4}>
                <Form.Item
                  label="Max bars"
                  name="maxDays"
                  dependencies={["minDays"]}
                  rules={[
                    { required: true, message: "Required" },
                    ({ getFieldValue }) => ({
                      validator: (_, value) => {
                        const minDays = getFieldValue("minDays");
                        if (
                          value == null ||
                          minDays == null ||
                          value >= minDays
                        )
                          return Promise.resolve();
                        return Promise.reject(new Error("Must be ≥ Min bars"));
                      },
                    }),
                  ]}
                >
                  <InputNumber min={1} step={1} style={{ width: "100%" }} />
                </Form.Item>
              </Col>
              <Col xs={12} sm={4}>
                <Form.Item
                  label="Mon/Wed/Fri expiries"
                  name="mwfExpiriesEnabled"
                  valuePropName="checked"
                >
                  <Switch />
                </Form.Item>
              </Col>
              <Col xs={12} sm={4}>
                <Form.Item
                  label="Exclude corrupted"
                  name="excludeCorrupted"
                  valuePropName="checked"
                  tooltip="A model trained on a symbol is biased when the requested date range overlaps its training window -- it's being tested on data it already saw. On by default; turn off to include those combos (flagged in the Corrupted column)."
                >
                  <Switch />
                </Form.Item>
              </Col>
            </Row>
            <Form.Item shouldUpdate>
              {() => (
                <Button type="primary" htmlType="submit" loading={isPending}>
                  Run Leaderboard
                </Button>
              )}
            </Form.Item>
            {time !== null && <p>Elapsed time: {time} seconds</p>}
            {progress !== null && (
              <>
                <div style={{ width: 170 }}>
                  <Progress
                    percent={progress}
                    status={isPending ? "active" : isError ? "exception" : ""}
                  />
                </div>
                {!isNaN(estimatedTime) && estimatedTime !== 0 && (
                  <span>
                    Estimated remaining time:{" "}
                    {formatMomentDurationFull(estimatedTime * 1000)}
                  </span>
                )}
              </>
            )}
          </Form>
        </GenericCard>

        <GenericCard title="Results" className="table-card" maxHeight={1000}>
          <div className="flex pb-4 gap-2">
            <Button
              type="default"
              onClick={() => setFilteredInfo(RECOMMENDED_FILTERS)}
            >
              Apply recommended filters
            </Button>
            <Button type="default" onClick={() => setFilteredInfo({})}>
              Reset filters
            </Button>
          </div>
          <AntTable
            rowKey={(row) => `${row.symbol}-${row.model?._id}`}
            dataSource={resultsWithFdr}
            columns={columns}
            onChange={(_pagination, filters) => setFilteredInfo(filters)}
            summary={(pageData) => (
              <Table.Summary fixed>
                <Table.Summary.Row>
                  <Table.Summary.Cell index={0}>Results:</Table.Summary.Cell>
                  <Table.Summary.Cell index={1} colSpan={40}>
                    {pageData.length}
                  </Table.Summary.Cell>
                </Table.Summary.Row>
              </Table.Summary>
            )}
          />
        </GenericCard>
      </div>

      {loadedAnalyses.length > 0 && (
        <div style={{ minHeight: 800 }}>
          <Tabs
            type="editable-card"
            items={analysisTabItems}
            onEdit={onEditAnalysisTab}
            hideAdd
            tabBarExtraContent={{
              right: (
                <Button type="primary" onClick={clearLoadedAnalyses}>
                  Clear
                </Button>
              ),
            }}
            activeKey={selectedAnalysisTab}
            onChange={setSelectedAnalysisTab}
          />
        </div>
      )}
    </div>
  );
};

export default OptionsLeaderboard;
